import { Box, LoadingOverlay, Title } from '@mantine/core'
import { useMutation } from '@tanstack/react-query'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router'
import { accountApi } from '@/api/accountApi'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { OtpHandler } from '@/components/login/OtpForm/OtpHandler'
import { SsoButtons } from '@/components/login/sso/SsoButtons'
import { RouterPath } from '@/router/paths'
import { useMfaLogin } from '@/session/useMfaLogin'
import { showToast } from '@/theme/notifications'
import { AuthLayout } from '../../layout/AuthLayout/AuthLayout'
import { SignUpForm, SubmitValues } from './SignUpForm/SignUpForm'

type SignUpFormHandlerProps = {
  email: string
  token: string
}

export const SignUpFormHandler = ({ email, token }: SignUpFormHandlerProps) => {
  const intl = useIntl()
  const navigate = useNavigate()

  const { login, state, isPending: isLoginPending } = useMfaLogin()

  const { mutate, isPending: isRegistrationPending } = useMutation({
    mutationFn: accountApi.completeRegistration,
    onSuccess: (_, variables) => {
      const { password } = variables

      login(
        { email, password },
        {
          onSuccess: () => {
            showToast(
              intl.formatMessage({ id: 'completeRegistration.accountCreated' }),
              'success'
            )
          },
          onError: () => {
            void navigate(RouterPath.login)
          }
        }
      )
    },
    onError: (err) => {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'completeRegistration.genericError' })
        ),
        'error'
      )
    }
  })

  const isPending = isLoginPending || isRegistrationPending

  const handleSubmit = (formValues: SubmitValues) => {
    if (isPending) {
      return
    }

    const { firstName, lastName, password } = formValues

    mutate({
      first_name: firstName,
      last_name: lastName,
      password,
      email,
      token
    })
  }

  if (state?.isMfaRequired) {
    return (
      <AuthLayout variant="large">
        <OtpHandler
          uid={state.uid}
          email={state.email}
          resendKey={state.resendKey}
        />
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <Box pos="relative">
        <LoadingOverlay visible={isPending} />

        <Title order={2} mb="xl">
          <FormattedMessage id="completeRegistration" />
        </Title>

        <SignUpForm initialEmail={email} onSubmit={handleSubmit} />

        <SsoButtons inviteToken={token} marginTop="md" />
      </Box>
    </AuthLayout>
  )
}
